// Navigation path to screen name mappings
export const pathToNameMap: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/records': 'Records',
  '/records/transactions': 'Transactions',
  '/records/transactions/new': 'New Transaction',
  '/records/transaction-entities': 'Transaction Entities',
  '/records/accounts': 'Accounts',
  '/records/accounts/ledgers': 'Ledgers',
  '/records/accounts/stock-valuation': 'Stock Valuation',
  '/records/accounts/overall-stock-valuation': 'Overall Stock Valuation',
  '/records/accounts/trading-accounts': 'Trading Accounts',
  '/records/accounts/overall-trading-account': 'Overall Trading Account',
  '/records/accounts/profit-loss': 'Profit & Loss',
  '/records/accounts/balance-sheets': 'Balance Sheets',
  '/profile': 'Profile',
  '/credit-points': 'Credit Points',
  '/subscription': 'Subscription',
  '/login': 'Login',
  '/signup': 'Sign Up',
  '/forgot-password': 'Forgot Password',
  '/': 'Login',
};

// Page hierarchy - defines parent-child relationships
// Key: child path, Value: parent path
export const pageHierarchy: Record<string, string> = {
  // Records children
  '/records/transactions': '/records',
  '/records/transactions/new': '/records/transactions',
  '/records/transactions/edit/:transactionId': '/records/transactions',
  '/records/transaction-entities': '/records',
  '/records/accounts': '/records',
  
  // Accounts children (under records/accounts)
  '/records/accounts/ledgers': '/records/accounts',
  '/records/accounts/stock-valuation': '/records/accounts',
  '/records/accounts/overall-stock-valuation': '/records/accounts', // Changed: goes back to accounts, not stock-valuation
  '/records/accounts/trading-accounts': '/records/accounts',
  '/records/accounts/overall-trading-account': '/records/accounts', // Changed: goes back to accounts, not trading-accounts
  '/records/accounts/profit-loss': '/records/accounts',
  '/records/accounts/balance-sheets': '/records/accounts',
  
  // Other top-level pages
  '/profile': '/dashboard',
  '/credit-points': '/dashboard',
  '/subscription': '/dashboard',
};

// Default parent for pages without explicit parent
export const DEFAULT_PARENT = '/dashboard';

// Helper function to get screen name from path
export const getScreenNameFromPath = (pathname: string): string => {
  // Remove trailing slashes and split
  const cleanPath = pathname.replace(/\/$/, '');
  
  // Check for edit transaction route
  if (cleanPath.startsWith('/records/transactions/edit/')) {
    return 'Edit Transaction';
  }

  // Check for single ledger detail route
  if (/^\/records\/accounts\/ledgers\/\d+$/.test(cleanPath)) {
    return 'Ledger';
  }

  // Check for single trading account detail route
  if (/^\/records\/accounts\/trading-accounts\/\d+$/.test(cleanPath)) {
    return 'Trading Account';
  }

  // Check for exact match first
  if (pathToNameMap[cleanPath]) {
    return pathToNameMap[cleanPath];
  }

  // Check for parent paths (for nested routes)
  const pathParts = cleanPath.split('/').filter(part => part);
  for (let i = pathParts.length; i > 0; i--) {
    const testPath = '/' + pathParts.slice(0, i).join('/');
    if (pathToNameMap[testPath]) {
      return pathToNameMap[testPath];
    }
  }

  // Default fallback
  return 'Balancio Pro';
};

// Helper function to get parent path for a given path
export const getParentPath = (pathname: string): string => {
  const cleanPath = pathname.replace(/\/$/, '');
  
  // Check for edit transaction route
  if (cleanPath.startsWith('/records/transactions/edit/')) {
    return '/records/transactions';
  }

  // Check for single ledger detail route
  if (/^\/records\/accounts\/ledgers\/\d+$/.test(cleanPath)) {
    return '/records/accounts/ledgers';
  }

  // Check for single trading account detail route
  if (/^\/records\/accounts\/trading-accounts\/\d+$/.test(cleanPath)) {
    return '/records/accounts/trading-accounts';
  }

  // Check explicit hierarchy first
  if (pageHierarchy[cleanPath]) {
    return pageHierarchy[cleanPath];
  }

  // Try to find parent by removing last segment
  const pathParts = cleanPath.split('/').filter(part => part);
  if (pathParts.length > 1) {
    // Remove last segment to get parent
    const parentPath = '/' + pathParts.slice(0, pathParts.length - 1).join('/');
    
    // Check if this parent exists in our path map
    if (pathToNameMap[parentPath] || pageHierarchy[parentPath]) {
      return parentPath;
    }
  }

  // Default to dashboard
  return DEFAULT_PARENT;
};

// Helper function to get parent page name for display
export const getParentPageName = (pathname: string): string => {
  const parentPath = getParentPath(pathname);
  return getScreenNameFromPath(parentPath);
};
